import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import { DatePicker, ColorPicker, ConfigProvider, Select, message, Modal, Table } from 'antd';
import { Input, Dropdown, Radio, Button } from '@/components/atoms';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import dayjs from 'dayjs';
import locale from 'antd/locale/ko_KR';
import AcademyStudentTable from '@/components/organisms/Table/AcademyStudentTable';
import { Icon } from '@/icons';
import { academyLearningScheduleApi, adminUnitApi, adminTextbookApi, academyStudentApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import { Typography } from 'antd';
const { Text } = Typography;
const { RangePicker } = DatePicker;

interface LearningEventDetail {
  id: string;
  title: string;
  start: string;
  end: string;
  studentName: string;
  studentId: string;
  birthDate: string;
  academy: string;
  description?: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  backgroundColor?: string;
  borderColor?: string;
  textbook?: string;
  unit?: string;
  maxUnitsPerDay?: number;
  scoring?: 'yes' | 'no';
  registrant?: string;
  registrationDate?: string;
  groupUuid?: string;
}

interface Student {
  id: string;
  userId?: string;
  name: string;
  academy?: string;
  grade?: string;
  status?: 'active' | 'inactive';
  no?: number;
  academyCode?: string;
  studentName?: string;
  birthDate?: string;
  loginId?: string;
  email?: string;
}

interface Unit {
  id: string;
  name: string;
  textbookId?: string;
  textbookName?: string;
  unitCategory?: string;
}

const AcademyLearningDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slugPrefix = getSlugPrefix(pathname);
  const { academyCode: sessionAcademyCode } = useSessionSlug();
  const academyCode = sessionAcademyCode || '';

  const [eventDetail, setEventDetail] = useState<LearningEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [selectedColor, setSelectedColor] = useState<string>('#1890ff');
  const [selectedUnitsState, setSelectedUnitsState] = useState<Unit[]>([]);
  const [learningTitle, setLearningTitle] = useState<string>('');
  const [maxUnitsPerDay, setMaxUnitsPerDay] = useState<number>(1);
  const [dateRange, setDateRange] = useState<any>(null);
  const [scoringRequired, setScoringRequired] = useState<boolean>(true);

  const typeOptions = useMemo(() => ([
    { value: '파닉스데이', label: '파닉스 데이' },
    { value: '스타터_파닉스', label: '스타터 파닉스' },
    { value: '스타터_뉘앙스', label: '스타터 뉘앙스' },
    { value: '스타터_스트럭처', label: '스타터 스트럭처' },
    { value: '에세이', label: '에세이' },
    { value: '통번역', label: '통번역' },
    { value: '리딩북', label: '리딩북' },
  ]), []);

  useEffect(() => {
    (async () => {
      try {
        const res = await academyLearningScheduleApi.detail(String(eventId));
        const data = res?.data || res;
        const d = data?.data || data;
        const event = {
          id: String(d.id),
          title: d.title || '',
          start: d.start,
          end: d.end,
          studentName: '',
          studentId: String(d.studentUserId || ''),
          birthDate: '',
          academy: String(d.academyId || ''),
          status: 'scheduled',
          backgroundColor: d.backgroundColor || '#1890ff',
          borderColor: d.borderColor || d.backgroundColor || '#1890ff',
          groupUuid: d.groupUuid || d.group_uuid || '',
        } as LearningEventDetail;
        setEventDetail(event);
        setSelectedColor(event.backgroundColor || '#1890ff');
        setLearningTitle(event.title || '');
        setMaxUnitsPerDay(d.maxUnitsPerDay || 1);
        setDateRange(event.start && event.end ? [dayjs(event.start), dayjs(event.end)] : null);
        setScoringRequired(d.scoringRequired !== false); // 서버에서 가져온 값 사용, 없으면 true

        // 학습 대상자 로드: 같은 학습 + 동일 기간(시작/종료 동일) 학생만 표시
        try {
          if (!academyCode) {
            setSelectedStudents([]);
            return;
          }
          // [Modified] 그룹 ID 기반으로 동일 학습 학생 목록 조회
          const sameRes = await academyLearningScheduleApi.listStudentsInSameLearning(String(eventId));
          const sameData = sameRes?.data || sameRes;
          const studentIdsRaw = sameData?.studentUserIds || sameData?.data?.studentUserIds || [];
          const studentIds: string[] = studentIdsRaw.map((id: any) => String(id));

          const resStudents = await academyStudentApi.list({ academyCode, page: 0, size: 1000 });
          const sd = resStudents?.data || resStudents;
          const p = sd?.data || sd;
          const content = p?.content || [];

          let all: any[] = content
            .filter((s: any) => studentIds.includes(String(s.userId)))
            .map((s: any, idx: number) => ({
              id: String(s.userId),
              userId: String(s.userId),
              no: idx + 1,
              academyCode: s.academyCode || academyCode,
              name: s.name,
              studentName: s.name,
              loginId: s.studentLoginId,
              birthDate: s.birthDate,
              email: s.email || '',
              registrationDate: s.registrationDate || dayjs().format('YYYY-MM-DD'),
              academy: academyCode,
              grade: s.gradeLabel || '',
              status: 'active' as const
            }));

          if (all.length < studentIds.length) {
            const foundIds = all.map(s => s.id);
            const missingIds = studentIds.filter(id => !foundIds.includes(id));
            const missingStudents = missingIds.map((sid: string, i: number) => ({
              id: sid,
              userId: sid,
              no: all.length + i + 1,
              academyCode: academyCode,
              name: `학생 ID: ${sid}`,
              studentName: `학생 ID: ${sid}`,
              birthDate: '',
              email: '',
              registrationDate: dayjs().format('YYYY-MM-DD'),
              academy: academyCode,
              grade: '',
              status: 'active' as const
            }));
            all = [...all, ...missingStudents];
          }

          setSelectedStudents(all as any);
        } catch (error) {
          console.error('Error loading students with same period:', error);
        }

        // 유형/유닛: 스케줄에 연결된 유닛 표시
        try {
          const unitIds: Array<{ id: number; orderIndex: number; textbookId?: number }> = d.units || [];
          if (unitIds.length) {
            const textbookNameMap: Record<string, string> = {};
            const textbookIdCandidates = unitIds
              .map((x) => x.textbookId)
              .filter((id): id is number => typeof id === 'number');
            const uniqueTextbookIds = Array.from(new Set<number>(textbookIdCandidates));
            if (uniqueTextbookIds.length) {
              await Promise.all(uniqueTextbookIds.map(async (textbookId) => {
                try {
                  const tr = await adminTextbookApi.detail(String(textbookId));
                  const td = tr?.data || tr;
                  const body = td?.data || td || {};
                  const name = body.name || body.title || '-';
                  textbookNameMap[String(textbookId)] = name;
                } catch (_) {
                  textbookNameMap[String(textbookId)] = '-';
                }
              }));
            }
            const details = await Promise.all(unitIds.map(async (x) => {
              let unitTitle = `유닛 #${x.id}`;
              let unitCategory = '-';
              const linkTextbookId = x.textbookId != null ? String(x.textbookId) : undefined;
              let textbookName = linkTextbookId ? textbookNameMap[linkTextbookId] : undefined;
              try {
                const ur = await adminUnitApi.detail(String(x.id));
                const ud = ur?.data || ur;
                const body = ud?.data || ud;
                unitTitle = body.title || body.name || unitTitle;
                unitCategory = body.unitCategory || unitCategory;
              } catch {}
              if (!textbookName) {
                try {
                  const tr = await adminTextbookApi.findByUnit(String(x.id));
                  const td = tr?.data || tr;
                  const list = td?.data || td || [];
                  if (Array.isArray(list) && list.length) textbookName = list[0].name || '-';
                } catch {}
              }
              return { id: String(x.id), name: unitTitle, textbookId: linkTextbookId, textbookName: textbookName || '-', unitCategory } as Unit;
            }));
            setSelectedUnitsState(details);
          } else {
            setSelectedUnitsState([]);
          }
        } catch (_) {}
      } catch (e) {
        console.error('Error loading event detail:', e);
        setEventDetail(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, academyCode]);

  const handleRemoveStudent = (studentId: string) => {
    setSelectedStudents((prev) => prev.filter((s) => s.id !== studentId));
    const removedCurrent = eventDetail && String(eventDetail.studentId) === String(studentId);
    if (removedCurrent) {
      message.info('현재 편집 중인 학생의 학습이 삭제되어 목록으로 이동합니다.');
      navigate(`${slugPrefix}/academy/learning/assign/list`);
    }
  };

  const handleSave = async () => {
    try {
      if (!eventDetail) {
        message.error('학습 정보를 불러올 수 없습니다.');
        return;
      }
      const targetExists = selectedStudents.some((s) => String(s.id) === String(eventDetail.studentId));
      if (!targetExists) {
        message.warning('현재 편집 중인 학생의 학습이 이미 삭제되었습니다.');
        navigate(`${slugPrefix}/academy/learning/assign/list`);
        return;
      }
      const payload = {
        title: learningTitle,
        startDate: dayjs(eventDetail.start).format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD') || dayjs(eventDetail.end).format('YYYY-MM-DD'),
        backgroundColor: selectedColor,
        borderColor: selectedColor,
        maxUnitsPerDay: maxUnitsPerDay,
        scoringRequired: scoringRequired,
      };
      await academyLearningScheduleApi.update?.(String(eventId), payload);
      try {
        const unitLinks = selectedUnitsState.map(u => ({
          unitId: Number(u.id),
          textbookId: u.textbookId ? Number(u.textbookId) : undefined,
        })).filter(link => !Number.isNaN(link.unitId));
        const unitIds = unitLinks.map(link => link.unitId);
        await academyLearningScheduleApi.linkUnits(String(eventId), { unitIds, unitLinks } as any);
      } catch (_) {}
      message.success('학습이 수정되었습니다.');
      navigate(`${slugPrefix}/academy/learning/assign/list`);
    } catch (error) {
      console.error('Validation failed:', error);
      message.error('수정 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteItem = (unitId: string) => {
    setSelectedUnitsState(prev => prev.filter(u => u.id !== unitId));
  };

  const groupedUnits = useMemo(() => {
    const map = new Map<string, { name: string; units: Unit[] }>();
    selectedUnitsState.forEach((unit) => {
      const name = unit.textbookName || '교재 미지정';
      if (!map.has(name)) {
        map.set(name, { name, units: [] });
      }
      map.get(name)!.units.push(unit);
    });
    return Array.from(map.values());
  }, [selectedUnitsState]);

  const handleUnitRemove = async (unitId: string | number) => {
    const numericId = Number(unitId);
    if (!Number.isNaN(numericId)) {
      try {
        const r = await academyLearningScheduleApi.hasUnitProgress?.(String(eventId), numericId);
        const has = (r?.data?.data?.hasProgress ?? r?.data?.hasProgress ?? r?.hasProgress) === true;
        if (has) {
          message.warning('이미 학습 진행된 유닛은 삭제할 수 없습니다.');
          return;
        }
      } catch (_) {}
    }

    if (selectedUnitsState.length === 1) {
      Modal.confirm({
        title: '해당 유닛까지 삭제할 경우 숙제 전체가 사라집니다. 삭제하시겠습니까?',
        okText: '삭제',
        okType: 'danger',
        cancelText: '취소',
        onOk: async () => {
          try {
            await academyLearningScheduleApi.linkUnits(String(eventId), { unitIds: [], unitLinks: [] } as any);
            await academyLearningScheduleApi.remove(String(eventId));
            message.success('숙제가 삭제되었습니다.');
            navigate(`${slugPrefix}/academy/learning/assign/list`);
          } catch (_) {}
        }
      });
      return;
    }
    handleDeleteItem(String(unitId));
  };

  if (loading) {
    return (
      <main className='flex flex-col flex-1 py-8'>
        <div className='p-5'>로딩 중...</div>
      </main>
    );
  }

  if (!eventDetail) {
    return (
      <main className='flex flex-col flex-1 py-8'>
        <div className='p-5'>이벤트를 찾을 수 없습니다.</div>
      </main>
    );
  }

  dayjs.locale('ko');
  return (
    <main className='flex flex-col flex-1 py-8'>
      <div className='flex flex-row justify-between items-center pb-5'>
        <h1 className='heading1'>학습 수정</h1>
      </div>
      <section>
        <ul>
          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>학습명</p>
            <Input 
              placeholder='학습명을 입력하세요' 
              className='max-w-[500px]'
              value={learningTitle}
              onChange={(e) => setLearningTitle((e.target as HTMLInputElement).value)}
            />
          </li>

          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>교재선택</p>
            <section className='flex flex-col gap-5 w-full'>
              {groupedUnits.length === 0 ? (
                <p className='text-gray-500'>선택된 항목이 없습니다.</p>
              ) : (
                groupedUnits.map((group, groupIndex) => (
                  <div key={`${group.name}-${groupIndex}`} style={{ marginTop: groupIndex === 0 ? 0 : 16 }}>
                    <Text>{group.name}</Text>
                    {(() => {
                      const totalUnits = group.units.length;
                      const groupDataSource = group.units.map((unit, idx) => ({
                        key: unit.id,
                        no: totalUnits - idx,
                        type: (unit as any).unitCategory || '-',
                        name: group.name || '-',
                        unitName: unit.name,
                        status: '등록된 항목',
                      }));
                      return (
                        <Table
                          dataSource={groupDataSource}
                          columns={[
                            { title: '순번', dataIndex: 'no', key: 'no', width: 60, align: 'center' },
                            { title: '유형', dataIndex: 'type', key: 'type', width: 200, align: 'center' },
                            { title: '교재', dataIndex: 'name', key: 'name', align: 'center' },
                            { title: '유닛명', dataIndex: 'unitName', key: 'unitName', align: 'center' },
                            { title: '학습상태', dataIndex: 'status', key: 'status', align: 'center' },
                            {
                              title: '삭제',
                              key: 'action',
                              width: 100,
                              align: 'center',
                              render: (_text: string, record: any) => (
                                <Button
                                  outline
                                  intent='danger'
                                  size='small'
                                  onClick={() => handleUnitRemove(record.key)}
                                >
                                  삭제
                                </Button>
                              )
                            },
                          ]}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: '선택된 항목이 없습니다.' }}
                        />
                      );
                    })()}
                  </div>
                ))
              )}
            </section>
          </li>

          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>일일 최대 학습 가능한 유닛 갯수</p>
            <div className='flex gap-3 items-center'>
              <Select 
                value={String(maxUnitsPerDay)} 
                onChange={(v) => setMaxUnitsPerDay(Number(v))} 
                style={{ width: 120 }} 
                size='large'
                options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
              />
              <span className='text-gray-500'>(1~10, 미소비 수량은 익일 가산)</span>
            </div>
          </li>

          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>학습 기간</p>
            <ConfigProvider
              locale={locale}
              theme={{
                token: {
                  colorPrimary: '#6366f1',
                  borderRadius: 10,
                  fontSize: 16,
                  colorBorder: '#E5E7EB',
                  colorBorderSecondary: '#E5E7EB',
                  colorTextPlaceholder: '#9CA3AF',
                  colorIcon: '#9CA3AF',
                },
              }}
            >
              <div className='flex gap-2 items-center'>
                <DatePicker.RangePicker
                  format="YYYY-MM-DD"
                  style={{ width: 500, height: 44 }}
                  value={dateRange}
                  disabledDate={(current) => {
                    try {
                      const start = dayjs(eventDetail?.start);
                      return !!current && current.isBefore(start, 'day');
                    } catch {
                      return false;
                    }
                  }}
                  onCalendarChange={(values, _ds, info) => {
                    if (!eventDetail) return;
                    if (info?.range === 'start') {
                      message.warning('시작일은 수정할 수 없습니다.');
                      const endVal = values?.[1] ? values?.[1] : (eventDetail.end ? dayjs(eventDetail.end) : dayjs(eventDetail.start));
                      setDateRange([dayjs(eventDetail.start), endVal]);
                    }
                  }}
                  onChange={(rng) => {
                    if (!eventDetail || !rng) return;
                    const fixedStart = dayjs(eventDetail.start);
                    const endVal = rng[1];
                    const safeEnd = endVal && endVal.isBefore(fixedStart, 'day') ? fixedStart : endVal;
                    setDateRange([fixedStart, safeEnd || fixedStart]);
                  }}
                />
              </div>
            </ConfigProvider>
          </li>

          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>캘린더 색상</p>
            <ConfigProvider
              locale={locale}
              theme={{
                token: {
                  colorPrimary: '#6366f1',
                  fontSize: 16,
                  colorBorder: '#E5E7EB',
                  colorBorderSecondary: '#E5E7EB',
                  colorTextPlaceholder: '#9CA3AF',
                  colorIcon: '#9CA3AF',
                  controlHeight: 32,
                },
              }}
            >
              <ColorPicker
                value={selectedColor}
                onChange={(color) => setSelectedColor(color.toHexString())}
                size="large"
              />
            </ConfigProvider>
          </li>
          
          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>채점 여부</p>
            <div className='flex gap-10'>
              <Radio label='채점함' name='scoring' onChange={() => setScoringRequired(true)} checked={scoringRequired}/>
              <Radio label='채점 불필요' name='scoring' onChange={() => setScoringRequired(false)} checked={!scoringRequired} />
            </div>
          </li>

          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>학습 대상</p>
            <section className='w-full'>
              <div className='mt-5 max-h-[400px] overflow-y-auto'>
                <AcademyStudentTable 
                  showSearch={false} 
                  data={selectedStudents as any} 
                  selectedStudents={selectedStudents as any} 
                  onStudentSelect={() => {}} 
                  onSelectAll={()=>{}} 
                  type='display' 
                  displayType='learning'
                  showPagination={false}
                  showAcademyCode={false}
                  onDelete={async (student) => {
                    const isLastStudent = selectedStudents.length <= 1;
                    Modal.confirm({
                      title: isLastStudent
                        ? '해당 학생까지 삭제할 경우 숙제 전체가 사라집니다. 삭제하시겠습니까?'
                        : '학생 삭제',
                      content: isLastStudent
                        ? '모든 학생을 삭제하면 학습이 더 이상 유지되지 않습니다.'
                        : '해당 학생을 삭제하시겠습니까?',
                      okText: '삭제',
                      cancelText: '취소',
                      onOk: async () => {
                        try {
                          const r = await academyLearningScheduleApi.hasStudentProgress?.(String(eventId), Number(student.id));
                          const has = (r?.data?.data?.hasProgress ?? r?.data?.hasProgress ?? r?.hasProgress) === true;
                          if (has) {
                            message.warning('학습을 진행한 학생은 제외할 수 없습니다.');
                            return;
                          }
                        } catch (_) {}
                        try {
                          if (!eventDetail?.title || !eventDetail?.start || !eventDetail?.end) {
                            message.error('학습 정보가 올바르지 않습니다.');
                            return;
                          }
                          const deletePayload = {
                            title: String(eventDetail.title),
                            startDate: dayjs(eventDetail.start).format('YYYY-MM-DD'),
                            endDate: dayjs(eventDetail.end).format('YYYY-MM-DD'),
                            studentUserId: Number(student.id),
                            groupUuid: eventDetail?.groupUuid
                          };
                          await academyLearningScheduleApi.deleteByStudent(deletePayload);
                          message.success('해당 학생의 동일 기간 학습이 삭제되었습니다.');
                        } catch (error) {
                          console.error('삭제 중 오류:', error);
                          message.error('삭제 중 오류가 발생했습니다.');
                          return;
                        }
                        handleRemoveStudent(student.id);
                      }
                    });
                  }}
                />
              </div>
            </section>
          </li>

          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>등록자</p>
            <p>{localStorage.getItem('academy_user_name') || '학원 관리자'}</p>
          </li>

          <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>등록일</p>
            <p>{eventDetail.start ? dayjs(eventDetail.start).format('YYYY-MM-DD') : new Date().toISOString().slice(0,10)}</p>
          </li>
        </ul>
        <div className='flex gap-2 justify-between my-5'>
          <Button outline className='w-28' onClick={() => navigate(`${slugPrefix}/academy/learning/assign/list`)}>뒤로가기</Button>
          <div className='flex gap-2'>
            <Button className='w-28' onClick={handleSave}>저장</Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AcademyLearningDetail;
